import { registerMockAPI } from '~/mocks/mockApis';

import { useAPI, useAsyncHandler } from '~/hooks/common';

import { API_ENDPOINT } from '~/constants/endpoint';

import type { TypeErrorResponseData } from '~/types/common.types';
import type { TypeGetOrderOutboundOrderCodeParams, TypeGetOrderOutboundOrderCodeResponse } from '~/types/order.types';

const useGetOrderOutboundOrderCode = () => {
  const { runAsync } = useAsyncHandler();

  const getOrderOutboundOrderCode = useAPI<TypeGetOrderOutboundOrderCodeParams, TypeGetOrderOutboundOrderCodeResponse>({
    endpoint: API_ENDPOINT.ORDER.GET_ORDER_OUTBOUND_ORDER_CODE,
    method: 'get',
  });

  const requestOrderDetail = async (
    params: TypeGetOrderOutboundOrderCodeParams,
    {
      onSuccess,
      onError,
    }: {
      onSuccess?: (data: TypeGetOrderOutboundOrderCodeResponse['data']) => void;
      onError?: (error: TypeErrorResponseData['error']) => void;
    }
  ) => {
    runAsync(() => getOrderOutboundOrderCode({ pathParams: `/${params.outboundOrderCode}` }), {
      onSuccess: ({ data }) => onSuccess?.(data),
      onError: (error: TypeErrorResponseData['error']) => {
        onError?.(error);
      },
    });
  };

  return { requestOrderDetail };
};

export default useGetOrderOutboundOrderCode;

const orderDetailResponse: TypeGetOrderOutboundOrderCodeResponse = {
  data: {
    order: {
      orderCode: 'OR20260518001',
      paymentAt: '2026-05-18 14:31:00',
      orderStoreName: '스마트스토어',
      orderItems: [
        { itemName: '[컬리] 유기농 바나나 1kg', itemId: 'G00000001', quantity: 3, cancelQuantity: 0 },
        { itemName: '[컬리] 무항생제 특란 20구', itemId: 'G00000002', quantity: 2, cancelQuantity: 1 },
        { itemName: '[컬리] 제주 감귤 2kg', itemId: 'G00000003', quantity: 1, cancelQuantity: 0 },
      ],
    },
    orderer: { id: 'e2ebuyer01', name: '김컬리', phoneNumber: '01012345678' },
    receiver: {
      name: '김컬리',
      phoneNumber: '01012345678',
      address: { primaryAddress: '서울특별시 강남구 테헤란로 133', secondAddress: '컬리타워 10층' },
      // 교환 신청 2단계와 상세 모달이 null 확인 없이 읽는다.
      deliveryRequest: {
        accessMethod: { code: 'PASSWORD', name: '공동현관 비밀번호' },
        accessDetail: '#1234',
        pickupPlaceType: { code: 'DOOR_FRONT', name: '문 앞' },
        pickupDetail: '',
        deliveryMessage: '부재 시 문 앞에 놓아주세요.',
      },
    },
    outbound: {
      outboundCode: 'OB202605180001',
      outboundStatus: { code: 'COMPLETED', name: '완료' },
      clusterCenter: { code: 'CC02', name: '김포' },
    },
    delivery: {
      operation: { code: 'DAWN', name: '새벽배송' },
      courier: { code: 'KURLY', name: '컬리' },
      invoices: [
        {
          invoiceNumber: '350012345678',
          deliveryStatus: { code: 'DELIVERED', name: '배송완료' },
          invoiceTrackingUrl: 'https://tracking.example.com/350012345678',
        },
        {
          invoiceNumber: '350012345679',
          deliveryStatus: { code: 'IN_TRANSIT', name: '배송중' },
          invoiceTrackingUrl: 'https://tracking.example.com/350012345679',
        },
      ],
    },
  },
};

registerMockAPI<TypeGetOrderOutboundOrderCodeResponse>({
  method: 'get',
  // 숫자가 하나 이상 들어간 코드만 받아 `/order/default-excel` 같은 형제 경로를 가로채지 않는다.
  endpoint: '/api-oms/order/:outboundOrderCode([A-Z]{0,}\\d[A-Z0-9]{0,})',
  label: ['출고주문/출고주문조회/상세', '반품교환/반품신청등록/주문상세', '반품교환/교환신청등록/주문상세'],
  scenarios: [
    { name: '정상 응답 — 상품 3건·송장 2건', response: orderDetailResponse },
    {
      name: '서버 에러 (500)',
      response: { error: { code: 500, message: '출고 주문 상세를 불러오지 못했습니다.' } },
      status: 500,
    },
  ],
});
